import './Select.less'

import { brandColors, formColors } from 'front-end/../shared/basicPalette'
import { Box, Flex } from 'front-end/src/components'
import React, { FC } from 'react'
import {
  components,
  default as ReactSelect,
  Props as ReactSelectProps
} from 'react-select'
import Creatable, { CreatableProps } from 'react-select/creatable'

import { t } from '@lingui/macro'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import { SelectContext } from './SelectContext'
import { cx } from '@emotion/css'

export function commonReactSelectStyles(variant: SelectVariant) {
  return {
    minWidth: 200,
    minHeight: '40px',
    borderWidth: variant === SelectVariant.WHITE ? 0 : '1px',
    borderStyle: 'solid',
    backgroundColor:
      variant === SelectVariant.WHITE ? brandColors.white : formColors.bgColor,
    transition: 'border-color 0.25s ease-in-out',
    ':focus-visible': {
      outline: 'none',
      backgroundColor:
        variant === SelectVariant.WHITE
          ? brandColors.grey[50]
          : formColors.focusBgColor
    },
    ':hover': {
      backgroundColor:
        variant === SelectVariant.WHITE
          ? brandColors.grey[50]
          : formColors.hoverBgColor
    }
  }
}
export function ReactSelectLooopStyles(variant: SelectVariant) {
  const reactSelectStyles = commonReactSelectStyles(variant)
  return {
    control: (styles, { isDisabled, isFocused }) => ({
      ...styles,
      ...reactSelectStyles,

      border: 'transparent',
      boxShadow: isDisabled
        ? brandColors.grey.lighter
        : isFocused
        ? '0 0 0 1px inset var(--chakra-colors-buttonPrimary-500)'
        : 'transparent',
      opacity: isDisabled ? 0.5 : 1,
      ':hover': {
        backgroundColor:
          variant === SelectVariant.WHITE
            ? brandColors.grey[50]
            : formColors.hoverBgColor,
        border: 'transparent',
        boxShadow: isFocused
          ? '0 0 0 1px inset var(--chakra-colors-buttonPrimary-500)'
          : 'transparent'
      }
    }),
    placeholder: (styles) => ({
      ...styles,
      color: brandColors.grey.mid
    }),
    menu: (styles) => ({
      ...styles,
      zIndex: 2
    }),
    option: (styles, { isDisabled, isFocused, isSelected }) => ({
      ...styles,
      color: isSelected ? brandColors.grey.dark : brandColors.grey.mid,
      backgroundColor: isDisabled
        ? null
        : isSelected
        ? brandColors.grey.lightest
        : isFocused
        ? brandColors.grey.lighter
        : null
    })
  }
}

export enum SelectVariant {
  GREY,
  WHITE
}

export type ReactSelectOption = {
  label: string | JSX.Element
  value: any
  [key: string]: unknown
}

export type SelectProps = ReactSelectProps<ReactSelectOption> & {
  controlWidth?: number
  minWidth?: number
  maxWidth?: number
  leftAddon?: string | JSX.Element
  variant?: SelectVariant
  dataTest?: string
}

const Control = ({ children, ...props }) => {
  const { leftAddon } = React.useContext(SelectContext)
  return (
    // @ts-ignore
    <components.Control {...props}>
      <Flex as="label" w="100%" align="center" className="selectControl">
        {props.selectProps.label && (
          <Box
            pl="8px"
            mr="8px"
            className={props.selectProps.labelClassName}
            color={brandColors.greyTransparent[50]}
          >
            {props.selectProps.label}
          </Box>
        )}

        {leftAddon && (
          <Box pl="8px" color={brandColors.greyTransparent[50]}>
            {leftAddon}
          </Box>
        )}

        {children}
      </Flex>
      <Box as="span" display="none" aria-live="polite">
        {t`${props.selectProps.options.length} option available.`}
      </Box>
    </components.Control>
  )
}

const MenuList = ({ children }) => (
  <Box
    maxH="300px"
    overflowY="auto"
    py="4px"
    role="listbox"
    position="relative"
    className="Select__menu-list"
  >
    {children}
  </Box>
)

const Option = ({
  isDisabled,
  isSelected,
  isFocused,
  children,
  innerProps
}) => (
  <Box
    color={brandColors.grey[500]}
    padding="8px 12px"
    w="100%"
    userSelect="none"
    pointerEvents={isDisabled ? 'none' : 'all'}
    opacity={isDisabled ? '50%' : 'initial'}
    role="option"
    cursor="pointer"
    bgColor={
      isSelected
        ? brandColors.grey[100]
        : isFocused
        ? brandColors.grey[50]
        : 'white'
    }
    className={cx('Select__option', {
      'Select__option--is-focused': isFocused,
      'Select__option--is-selected': isSelected
    })}
    {...innerProps}
  >
    {children}
  </Box>
)

// @ts-ignore
const Input = ({ ...props }) => <components.Input {...props} role="combobox" />

/*
 * Styled wrapper around react-select
 */
export const Select: FC<SelectProps> = ({
  controlWidth,
  minWidth,
  maxWidth,
  leftAddon,
  styles = {},
  variant = SelectVariant.GREY,
  dataTest = 'Select',
  ...rest
}) => {
  const reactSelectStyles = ReactSelectLooopStyles(variant)
  const context = React.useMemo(
    () => ({
      leftAddon
    }),
    [leftAddon]
  )
  return (
    <SelectContext.Provider value={context}>
      <Box data-test={dataTest}>
        <ReactSelect
          classNamePrefix="Select"
          placeholder={t`Not selected`}
          components={{
            Control,
            Input,
            IndicatorSeparator: () => null,
            MenuList,
            Option
          }}
          styles={{
            ...reactSelectStyles,
            control: (...params) => ({
              ...reactSelectStyles.control(...params),
              width: controlWidth,
              minWidth: minWidth,
              maxWidth: maxWidth
            }),
            ...styles
          }}
          {...rest}
        />
      </Box>
    </SelectContext.Provider>
  )
}

/*
 * styled wrapper around react-select
 */
export const CreatableSelect: FC<
  SelectProps &
    CreatableProps<
      {
        value: string | JSX.Element
        label: string
      },
      boolean
    > & { styles: any }
> = ({ styles = {}, ...rest }) => {
  const { theme } = useLooopTheme()

  return (
    // @ts-expect-error
    <Creatable
      classNamePrefix="Select"
      placeholder={t`Not selected`}
      styles={{
        control: (styles, { isDisabled, isFocused }) => ({
          ...styles,
          // For some reason radius styles don't get applied. Well let's keep them here anyway-may be a bug in react-select. We override it in Timepicker anyway with emotion css
          borderBottomLeftRadius: 4,
          borderTopLeftRadius: 4,
          borderColor: isFocused
            ? theme.colors.buttonPrimary.default
            : brandColors.grey[50],
          backgroundColor: isDisabled
            ? brandColors.grey[100]
            : brandColors.grey[50],
          boxShadow: 'none',
          '&:hover': {
            borderColor: isFocused
              ? theme.colors.buttonPrimary.default
              : 'transparent'
          }
        }),
        placeholder: (styles) => ({
          ...styles,
          color: '#68808b'
        }),
        container: (styles) => ({
          ...styles,
          borderBottomLeftRadius: 4,
          borderTopLeftRadius: 4
        }),
        ...styles
      }}
      {...rest}
    />
  )
}

// TODO: remove (prefer named)
export default Select
